import { useDispatch, useSelector } from "react-redux"
import { CloseIcon } from "../../Shared/Components/SvgIcons"
import { setModalData } from "../../Shared/SharedSlices/ModalSlice"
import { RootState } from "../../../App/store"
import Button from "../../Shared/Primitive/Button"
import { deleteEventRequest, exportEventRequest } from "../RoomServiceRequests/EventServiceRequest"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import { useParams } from "react-router"
import { RoleType } from "../../../App/enums"
import { setRoomEventsData } from "../RoomSlices/RoomEventsSlice"
import { useState, useEffect, useMemo } from "react";
import Image from "../../Shared/Primitive/Image"
import { formatDate } from "../../../App/Helpers/Helpers"
import { setRoomData } from "../../Shared/SharedSlices/RoomSlice"
import { Capacitor } from "@capacitor/core"
import { CapacitorCalendar } from "@ebarooni/capacitor-calendar"
import { ToastService } from "../../../App/Helpers/ToastService"

function getLinkLabel(url: string) {
    try {
        const parsedUrl = new URL(url)
        return parsedUrl.hostname.replace("www.", "")
    } catch {
        return url
    }
}

function createEventFileName(title?: string) {
    const fallbackTitle = (title || "Kalendereintrag").trim()
    const safeTitle = fallbackTitle
        .replace(/[^a-zA-Z0-9äöüÄÖÜß\-_ ]/g, "")
        .replace(/\s+/g, "-")
        .slice(0, 80)

    return `${safeTitle || "Kalendereintrag"}.ics`
}

function createEventLocationLabel(locationType?: string, locationDetails?: string) {
    return [locationType, locationDetails].filter(Boolean).join(" • ")
}

function stripHtml(input?: string) {
    if (!input) {
        return ""
    }

    return input
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim()
}


function RoomEventModal() {
    const event = useSelector((state: RootState) => state.roomEventsSlice.selectedItem)
    const roomEventsData = useSelector((state: RootState) => state.roomEventsSlice)
    const roomDataState = useSelector((state: RootState) => state.roomSlice.roomSlice.room)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const { id } = useParams()
    const dispatch = useDispatch()
    const translation = useSelector(selectTranslations)
    const isNativeApp = Capacitor.isNativePlatform()
    const [canManageEvent, setCanManageEvent] = useState(false)
    const dateTimeLabel = useMemo(() => {
        if (!event?.start_date) {
            return ""
        }

        const startDate = formatDate(event.start_date, "dd.MM.yyyy")
        const startTime = formatDate(event.start_date, "HH:mm")

        if (!event.end_date || event.end_date === event.start_date) {
            return `${startDate} • ${startTime} Uhr`
        }

        const endDate = formatDate(event.end_date, "dd.MM.yyyy")
        const endTime = formatDate(event.end_date, "HH:mm")

        if (startDate === endDate) {
            return `${startDate} • ${startTime}–${endTime} Uhr`
        }

        return `${startDate} ${startTime} Uhr – ${endDate} ${endTime} Uhr`
    }, [event?.start_date, event?.end_date])
    const eventOwnerLabel = (() => {
        const owner = event?.content_object as { name?: string; first_name?: string; last_name?: string } | undefined
        if (!owner) {
            return ""
        }

        if (owner.name?.trim()) {
            return owner.name.trim()
        }

        const fullName = [owner.first_name, owner.last_name].filter(Boolean).join(" ").trim()
        return fullName
    })()

    useEffect(() => {
        if (!event || !event.id) return;
        switch (role) {
            case RoleType.ORGANIZATION:
                setCanManageEvent(Number(id) === event.content_object.id && event.content_type === "organization")
                break;
            case RoleType.USER:
                setCanManageEvent(userDataState.id === event.content_object.id && event.content_type === "user")
                break;
            default:
                break;
        }
    }, [event, id, role, userDataState.id])

    const onModalClose = () => {
        dispatch(setModalData({ open: false, roomEventModal: false }))
        dispatch(setRoomEventsData({ ...roomEventsData, selectedItem: undefined }))
    }

    const onDeleteButtonClick = () => {
        if (!event) return;
        const orgId = role === RoleType.ORGANIZATION ? Number(id) : undefined;
        deleteEventRequest(roomDataState.id, event.id, orgId)
            .then(() => {
                dispatch(setRoomEventsData({ ...roomEventsData, selectedItem: undefined, items: roomEventsData.items.filter((item) => item.id !== event.id) }))
                dispatch(setModalData({ open: false, roomEventModal: false }))
                dispatch(setRoomData({
                    ...roomDataState,
                    total_events: (roomDataState.total_events ?? 0) - 1
                }))
            }
            )
            .catch((error) => {
                console.error("Failed to delete event:", error)
            }
            )
    }

    const onEditButtonClick = () => {
        if (!event) return;
        dispatch(setModalData({ open: true, roomCreateEventModal: { edit: true } }))
        //dispatch(setModalData({ open: true, roomEventEditModal: true, eventId: event.id, orgId: orgId }))
    }

    const onDownloadIcsButtonClick = async () => {
        if (!event) return;

        try {
            if (isNativeApp) {
                const permission = await CapacitorCalendar.requestWriteOnlyCalendarAccess()

                if (permission.result !== "granted") {
                    ToastService.warning({
                        title: "Kalenderzugriff nicht erlaubt",
                        description: "Bitte erlaube den Kalenderzugriff, um das Event zu speichern.",
                    })
                    return
                }

                const startDate = new Date(event.start_date).getTime()
                const endDate = new Date(event.end_date || event.start_date).getTime()
                const location = createEventLocationLabel(event.location_type, event.location_details)
                const primaryUrl = event.registration_link || event.access_link || event.website || undefined
                const descriptionParts = [stripHtml(event.description)]

                if (event.mail) {
                    descriptionParts.push(`Kontakt: ${event.mail}`)
                }

                if (primaryUrl) {
                    descriptionParts.push(`Link: ${primaryUrl}`)
                }

                await CapacitorCalendar.createEventWithPrompt({
                    title: event.title,
                    location: location || undefined,
                    startDate,
                    endDate,
                    description: descriptionParts.filter(Boolean).join("\n\n") || undefined,
                    url: primaryUrl,
                })
                return
            }

            const response = await exportEventRequest(event.room.id, event.id);
            const blob = new Blob([response.data], { type: "text/calendar" });
            const fileName = createEventFileName(event.title)

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download ICS file:", error);
            ToastService.error({
                title: "Kalendereintrag konnte nicht geoeffnet werden",
                description: "Bitte versuche es erneut.",
            })
        }
    }


    return (
        <div className="event-modal event-modal--view">
            <div className="event-modal__content">
                <div className="event-modal__upper-panel">
                    <div className="event-modal__title">{event?.title}</div>
                    <div onClick={onModalClose} className="event-modal__close-icon">
                        <CloseIcon />
                    </div>
                </div>
                <div className="event-modal__divider"></div>
                <div className="event-modal__scrollable-content">
                    <div className="event-modal__event-panel">
                        <div className="event-modal__hero">
                            <div className="event-modal__image">
                                {event?.image ? (<Image src={event.image} className="event-modal__event-image" />) : (
                                    <Image src="/event_default.png" className="event-modal__event-image" />
                                )}
                            </div>
                            <div className="event-modal__hero-meta">
                                {dateTimeLabel && <div className='event-modal__date-range'>{dateTimeLabel}</div>}
                                {event?.formats && (
                                    <div className="event-modal__hero-chip">{event.formats}</div>
                                )}
                                {event?.location_type && (
                                    <div className="event-modal__hero-chip">{event.location_type}</div>
                                )}
                            </div>
                        </div>

                        <div className="event-modal__text">Beschreibung</div>
                        <div className="event-modal__description">
                            {event?.description ? (
                                <div dangerouslySetInnerHTML={{ __html: event.description }} />
                            ) : (
                                <div className="event-modal__detail">Keine Beschreibung hinterlegt.</div>
                            )}
                        </div>

                        {event?.keywords && event.keywords.length > 0 && (
                            <div className="event-modal__tags-content">
                                {event.keywords.map((keyword, index) => (
                                    <div key={index} className="event-modal__tag">{keyword}</div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="event-modal__details-section">
                        <div className="event-modal__text">Eckdaten</div>

                        {eventOwnerLabel && (
                            <div className="event-modal__detail-row">
                                <div className="event-modal__detail-label">Erstellt von</div>
                                <div className="event-modal__detail-value">{eventOwnerLabel}</div>
                            </div>
                        )}

                        {event?.location_type && (
                            <div className="event-modal__detail-row">
                                <div className="event-modal__detail-label">Veranstaltungsort</div>
                                <div className="event-modal__detail-value">
                                    {event.location_type}
                                    {event.location_details ? ` • ${event.location_details}` : ""}
                                </div>
                            </div>
                        )}

                        {event?.admission && (
                            <div className="event-modal__detail-row">
                                <div className="event-modal__detail-label">Eintritt</div>
                                <div className="event-modal__detail-value">{event.admission}</div>
                            </div>
                        )}

                        {(event?.registration_link || event?.access_link || event?.mail || event?.website) && (
                            <div className="event-modal__text">Links & Kontakt</div>
                        )}

                        {event?.registration_link && (
                            <div className="event-modal__detail-row">
                                <div className="event-modal__detail-label">{translation.registrationLink}</div>
                                <div className="event-modal__detail-value">
                                    <a href={event.registration_link} target="_blank" rel="noopener noreferrer">{getLinkLabel(event.registration_link)}</a>
                                </div>
                            </div>
                        )}

                        {event?.access_link && (
                            <div className="event-modal__detail-row">
                                <div className="event-modal__detail-label">{translation.accessLink}</div>
                                <div className="event-modal__detail-value">
                                    <a href={event.access_link} target="_blank" rel="noopener noreferrer">{getLinkLabel(event.access_link)}</a>
                                </div>
                            </div>
                        )}

                        {event?.mail && (
                            <div className="event-modal__detail-row">
                                <div className="event-modal__detail-label">E-Mail</div>
                                <div className="event-modal__detail-value"><a href={`mailto:${event.mail}`}>{event.mail}</a></div>
                            </div>
                        )}

                        {event?.website && (
                            <div className="event-modal__detail-row">
                                <div className="event-modal__detail-label">Website</div>
                                <div className="event-modal__detail-value">
                                    <a href={event.website} target="_blank" rel="noopener noreferrer">{getLinkLabel(event.website)}</a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="event-modal__footer">
                    <div className="event-modal__button-container">
                        <div className='room-events-header__ics-button' onClick={onDownloadIcsButtonClick}>
                            <Button
                                className="button button--border-radius-05 button--color-green button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-small button--font-weight-400"
                            >
                                <span className="event-modal__button-with-icon">
                                    <svg className="event-modal__button-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path
                                            d="M10 2.16663C10.4602 2.16663 10.8333 2.53972 10.8333 2.99996V10.1559L13.2441 7.74487C13.5695 7.41943 14.0972 7.41943 14.4226 7.74487C14.7481 8.07032 14.7481 8.59795 14.4226 8.9234L10.5893 12.7567C10.2638 13.0822 9.73621 13.0822 9.41074 12.7567L5.57741 8.9234C5.25198 8.59795 5.25198 8.07032 5.57741 7.74487C5.90285 7.41943 6.43049 7.41943 6.75593 7.74487L9.16666 10.1556V2.99996C9.16666 2.53972 9.53976 2.16663 10 2.16663Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            d="M4.16666 14.1666C4.16666 13.7064 3.79357 13.3333 3.33333 13.3333C2.87309 13.3333 2.5 13.7064 2.5 14.1666V15.8333C2.5 17.214 3.61929 18.3333 5 18.3333H15C16.3807 18.3333 17.5 17.214 17.5 15.8333V14.1666C17.5 13.7064 17.1269 13.3333 16.6667 13.3333C16.2064 13.3333 15.8333 13.7064 15.8333 14.1666V15.8333C15.8333 16.2935 15.4602 16.6666 15 16.6666H5C4.53976 16.6666 4.16666 16.2935 4.16666 15.8333V14.1666Z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                    <span>{isNativeApp ? "Im Kalender speichern" : "Download"}</span>
                                </span>
                            </Button>
                        </div>

                        <div className='room-events-header__edit-button' onClick={onEditButtonClick}>
                            <Button disabled={!canManageEvent}
                                className="button button--border-radius-05 button--color-gold button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-small button--font-weight-400"
                            >
                                {translation.edit}
                            </Button>
                        </div>

                        <div className='room-events-header__delete-button' onClick={onDeleteButtonClick}>
                            <Button disabled={!canManageEvent}
                                className="button button--border-radius-05 button--color-red button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-small button--font-weight-400"
                            >
                                {translation.delete}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default RoomEventModal
